import React, { useState, useEffect, useCallback } from 'react';
import {
  Row, Col, Card, Input, Select, Spin, Empty, Tag, Space,
  Typography, Button, Drawer, Descriptions, Rate, Divider,
  Carousel, Switch, message,
} from 'antd';
import {
  SearchOutlined, EnvironmentOutlined, GlobalOutlined, AimOutlined,
  CarOutlined, ThunderboltOutlined, DollarOutlined, ShoppingCartOutlined,
} from '@ant-design/icons';
import { hotelAPI } from '../services/api';
import { useTranslation } from 'react-i18next';

const { Text, Title, Paragraph } = Typography;
const { Option } = Select;

const fmtPrice = (p) => {
  if (!p) return '-';
  if (p >= 1000000) return `฿${(p / 1000000).toFixed(1)}M`;
  if (p >= 1000) return `฿${(p / 1000).toFixed(0)}K`;
  return `฿${p.toLocaleString()}`;
};

const POI_ICONS = {
  tennis: '🎾', pickleball: '🏓', golf: '⛳', gym: '💪',
  park: '🌳', mall: '🏬', shopping_street: '🛍️',
};
const POI_COLORS = {
  tennis: 'green', pickleball: 'volcano', golf: 'gold', gym: 'orange',
  park: 'green', mall: 'magenta', shopping_street: 'purple',
};

function HotelCard({ h, onOpen }) {
  const { t } = useTranslation();
  return (
    <Card
      hoverable
      onClick={() => onOpen(h)}
      cover={
        h.photos && h.photos.length > 0 ? (
          <img
            alt={h.name}
            src={h.photos[0]}
            style={{ height: 180, objectFit: 'cover' }}
            onError={e => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div style={{ height: 180, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <EnvironmentOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
          </div>
        )
      }
    >
      <Title level={5} style={{ margin: 0, minHeight: 44, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {h.name_en || h.name}
      </Title>
      {h.name_zh && h.name_zh !== (h.name_en || h.name) && (
        <div style={{ marginTop: 2 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>{h.name_zh}</Text>
        </div>
      )}
      <div style={{ marginTop: 4 }}>
        <Tag color="orange">🏨 {t('hotel.hotel', 'Hotel')}</Tag>
        {(h.area_name_en || h.area_name) && <Tag>{h.area_name_en || h.area_name}</Tag>}
      </div>
      <div style={{ marginTop: 8 }}>
        <Text strong style={{ color: '#e8363a', fontSize: 17 }}>
          {fmtPrice(h.price_per_night)}
        </Text>
        <Text type="secondary" style={{ fontSize: 12 }}> / {t('hotel.night', 'night')}</Text>
      </div>
      {h.rating ? (
        <div style={{ marginTop: 4 }}>
          <Rate disabled allowHalf defaultValue={h.rating} style={{ fontSize: 13 }} />
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>{h.rating}</Text>
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 2 }}>（{h.rating_count} {t('hotel.ratings', 'ratings')}）</Text>
        </div>
      ) : (
        <div style={{ marginTop: 4 }}><Text type="secondary" style={{ fontSize: 12 }}>{t('hotel.noRating', 'No ratings yet')}</Text></div>
      )}
      <div style={{ marginTop: 6 }}>
        {(h.bts_station || h.mrt_station) && (
          <Tag color={h.bts_station ? 'green' : 'purple'} style={{ marginRight: 4 }}>
            <ThunderboltOutlined /> {h.bts_station || h.mrt_station}
            {h.transit_minutes ? ` · ${h.transit_minutes} ${t('hotel.min', 'min')}` : ''}
          </Tag>
        )}
        {h.airport_drive_minutes ? (
          <Tag color="blue"><CarOutlined /> {t('hotel.airport', 'Airport')} {h.airport_drive_minutes} {t('hotel.min', 'min')}</Tag>
        ) : null}
      </div>
      <div style={{ marginTop: 6 }}>
        {h.has_convenience_store && <Tag color="cyan"><ShoppingCartOutlined /> {t('hotel.convenienceStore', 'Convenience Store')}</Tag>}
        {(h.nearby_pois || []).length > 0 && (
          <span>
            {[...new Set(h.nearby_pois.map(p => p.category))]
              .filter(c => ['tennis', 'pickleball', 'golf', 'gym', 'park'].includes(c))
              .slice(0, 4)
              .map(c => (
                <Tag key={c} color={POI_COLORS[c]} style={{ marginRight: 4 }}>
                  {POI_ICONS[c] || '📍'} {c === 'tennis' ? t('hotel.poiTennis', 'Tennis') : c === 'pickleball' ? t('hotel.poiPickleball', 'Pickleball') : c === 'golf' ? t('hotel.poiGolf', 'Golf') : c === 'gym' ? t('hotel.poiGym', 'Gym') : t('hotel.poiPark', 'Park')}
                </Tag>
              ))}
          </span>
        )}
      </div>
    </Card>
  );
}

function HotelSearchPage() {
  const { t } = useTranslation();
  const [areas, setAreas] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [area, setArea] = useState('');
  const [keyword, setKeyword] = useState('');
  const [minRating, setMinRating] = useState(null);
  const [maxPrice, setMaxPrice] = useState(null);
  const [withConvenience, setWithConvenience] = useState(false);
  const [poiCats, setPoiCats] = useState([]);
  const [poiKeys, setPoiKeys] = useState([]);
  const [sortBy, setSortBy] = useState('rating');
  const [sortOrder, setSortOrder] = useState('desc');
  const [limit, setLimit] = useState(24);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchAreas = useCallback(async () => {
    try {
      const res = await hotelAPI.getAreas();
      setAreas(res.data?.items || []);
    } catch (e) {
      console.error('❌ load hotel areas failed:', e);
      message.error(t('hotel.loadAreasFailed', 'Failed to load popular areas'));
    }
  }, [t]);

  const fetchPoiCats = useCallback(async () => {
    try {
      const res = await hotelAPI.getPoiCategories();
      setPoiCats(res.data?.items || []);
    } catch (e) {
      console.error('❌ load POI categories failed:', e);
    }
  }, []);

  const fetchHotels = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort_by: sortBy, sort_order: sortOrder, limit };
      if (area) params.area = area;
      if (keyword) params.keyword = keyword;
      if (minRating != null) params.min_rating = minRating;
      if (maxPrice != null) params.max_price = maxPrice;
      if (withConvenience) params.with_convenience = true;
      if (poiKeys.length) params.poi = poiKeys.join(',');
      const res = await hotelAPI.list(params);
      setHotels(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      console.error('❌ load hotels failed:', e);
      message.error(e.message || t('hotel.loadHotelsFailed', 'Failed to load hotels'));
    } finally {
      setLoading(false);
    }
  }, [area, keyword, minRating, maxPrice, withConvenience, poiKeys, sortBy, sortOrder, limit, t]);

  useEffect(() => { fetchAreas(); }, [fetchAreas]);
  useEffect(() => { fetchPoiCats(); }, [fetchPoiCats]);
  useEffect(() => { fetchHotels(); }, [fetchHotels]);

  const openDetail = async (h) => {
    setDetail(h);
    setDetailOpen(true);
    try {
      const res = await hotelAPI.get(h.id);
      if (res.data) setDetail(res.data);
    } catch (e) {
      console.error('load hotel detail failed:', e);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await hotelAPI.sync();
      message.success(`${t('hotel.syncDone', 'Hotel data sync complete')}（${t('hotel.added', 'added')} ${res.data?.created ?? 0} / ${t('hotel.updated', 'updated')} ${res.data?.updated ?? 0}）`);
      fetchAreas();
      fetchHotels();
    } catch (e) {
      console.error('❌ sync hotels failed:', e);
      message.error(e.message || t('hotel.syncFailed', 'Failed to sync hotels'));
    } finally {
      setSyncing(false);
    }
  };

  const mapsUrl = (h) => h.google_maps_url ||
    (h.latitude && h.longitude
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${h.latitude},${h.longitude}`)}`
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(h.name)}`);

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: 16 }}>
          <Space>
            <Title level={4} style={{ margin: 0 }}>🏨 {t('hotel.pageTitle', 'Bangkok Hotel Search')}</Title>
            <Text type="secondary">{t('hotel.totalLabel', 'Total')} {total} {t('hotel.hotels', 'hotels')} · {t('hotel.byArea', 'explore by popular areas')}</Text>
          </Space>
          <Button icon={<AimOutlined />} onClick={handleSync} loading={syncing}>{t('hotel.syncBtn', 'Sync Hotel Data')}</Button>
        </Space>

        {/* Popular Areas */}
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ marginRight: 8 }}>{t('hotel.hotAreas', 'Popular Areas:')}</Text>
          <Tag.CheckableTag
            checked={!area}
            onChange={() => setArea('')}
            style={{ fontSize: 13, padding: '2px 10px', marginBottom: 4 }}
          >
            {t('hotel.all', 'All')}
          </Tag.CheckableTag>
          {areas.map(a => (
            <Tag.CheckableTag
              key={a.key}
              checked={area === a.key}
              onChange={() => setArea(area === a.key ? '' : a.key)}
              style={{ fontSize: 13, padding: '2px 10px', marginBottom: 4 }}
            >
              {a.name_en || a.name_zh} {a.count ? `(${a.count})` : ''}
            </Tag.CheckableTag>
          ))}
        </div>

        {/* Filters */}
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder={t('hotel.searchPlaceholder', 'Search hotel name')}
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              style={{ width: '100%' }}
              value={minRating}
              placeholder={t('hotel.minRating', 'Min Rating')}
              allowClear
              onChange={setMinRating}
            >
              <Option value={4.5}>4.5+ {t('hotel.ratingExcellent', 'Excellent')}</Option>
              <Option value={4.0}>4.0+ {t('hotel.ratingGreat', 'Great')}</Option>
              <Option value={3.5}>3.5+ {t('hotel.ratingGood', 'Good')}</Option>
            </Select>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              style={{ width: '100%' }}
              value={maxPrice}
              placeholder={t('hotel.budgetPerNight', 'Budget / Night')}
              allowClear
              onChange={setMaxPrice}
            >
              <Option value={3000}>฿3,000 {t('hotel.below', 'below')}</Option>
              <Option value={5000}>฿5,000 {t('hotel.below', 'below')}</Option>
              <Option value={8000}>฿8,000 {t('hotel.below', 'below')}</Option>
              <Option value={10000}>฿10,000 {t('hotel.below', 'below')}</Option>
            </Select>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Space>
              <Text>{t('hotel.convenienceStore', 'Convenience Store')}</Text>
              <Switch checked={withConvenience} onChange={setWithConvenience} />
            </Space>
          </Col>
          <Col xs={12} sm={6} md={6}>
            <Select
              placeholder={t('hotel.poiPlaceholder', 'Nearby Facilities')}
              style={{ width: '100%' }}
              mode="multiple"
              value={poiKeys}
              onChange={setPoiKeys}
              maxTagCount={3}
              allowClear
            >
              {poiCats.map(c => (
                <Option key={c.key} value={c.key}>
                  {POI_ICONS[c.key] || '📍'} {c.label_zh} / {c.label_en}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={6} md={6}>
            <Select style={{ width: '100%' }} value={sortBy} onChange={setSortBy}>
              <Option value="rating">{t('hotel.sortRating', 'Highest Rating')}</Option>
              <Option value="price">{t('hotel.sortPrice', 'Price')}</Option>
              <Option value="airport_drive">{t('hotel.sortAirport', 'Nearest Airport')}</Option>
              <Option value="name">{t('hotel.sortName', 'Name')}</Option>
            </Select>
          </Col>
        </Row>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
        ) : hotels.length === 0 ? (
          <Empty description={t('hotel.noResults', 'No matching hotels')} />
        ) : (
          <Row gutter={[16, 16]}>
            {hotels.map(h => (
              <Col key={h.id} xs={24} sm={12} md={8} lg={6}>
                <HotelCard h={h} onOpen={openDetail} />
              </Col>
            ))}
          </Row>
        )}
      </Card>

      {/* Detail Drawer */}
      <Drawer
        title={detail ? (detail.name_en || detail.name) : ''}
        width={680}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        extra={detail && (
          <Space>
            <Button
              type="primary"
              icon={<GlobalOutlined />}
              href={mapsUrl(detail)}
              target="_blank"
            >
              Google Map
            </Button>
            {detail.booking_url && (
              <Button icon={<DollarOutlined />} href={detail.booking_url} target="_blank">
                {t('hotel.viewRates', 'View Rates')}
              </Button>
            )}
          </Space>
        )}
      >
        {detail && (
          <>
            <div style={{ marginBottom: 12 }}>
              {detail.name_zh && detail.name_zh !== (detail.name_en || detail.name) && (
                <Title level={5} style={{ margin: 0, color: '#1677ff' }}>{detail.name_zh}</Title>
              )}
              <Text type="secondary">{detail.name_en || detail.name}</Text>
            </div>
            {detail.photos && detail.photos.length > 0 && (
              <Carousel autoplay dots style={{ borderRadius: 8, overflow: 'hidden' }}>
                {detail.photos.map((p, i) => (
                  <div key={i}>
                    <img src={p} alt={`${detail.name} ${i + 1}`} style={{ width: '100%', height: 320, objectFit: 'cover' }} />
                  </div>
                ))}
              </Carousel>
            )}

            <Divider />

            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label={t('hotel.area', 'Area')}>
                {detail.area_name_en || detail.area_name || '-'}
                {detail.area_name_zh && detail.area_name_zh !== detail.area_name_en ? `（${detail.area_name_zh}）` : ''}
              </Descriptions.Item>
              <Descriptions.Item label={t('hotel.nightPrice', 'Price / Night')}>
                <Text strong style={{ color: '#e8363a' }}>{fmtPrice(detail.price_per_night)}</Text> / {t('hotel.night', 'night')}
              </Descriptions.Item>
              <Descriptions.Item label={t('hotel.rating', 'Rating')} span={2}>
                <Rate disabled allowHalf value={detail.rating} style={{ fontSize: 14 }} />
                <Text type="secondary"> {detail.rating}（{detail.rating_count} {t('hotel.ratings', 'ratings')}）</Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('hotel.address', 'Address')} span={2}>{detail.address || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('hotel.nearestStation', 'Nearest Station')}>
                {detail.bts_station
                  ? <Tag color="green"><ThunderboltOutlined /> BTS {detail.bts_station}</Tag>
                  : null}
                {detail.mrt_station
                  ? <Tag color="purple"><ThunderboltOutlined /> MRT {detail.mrt_station}</Tag>
                  : null}
                {!detail.bts_station && !detail.mrt_station ? '—' : null}
              </Descriptions.Item>
              <Descriptions.Item label={t('hotel.walkToStation', 'Walk to Station')}>
                {detail.transit_minutes ? `${detail.transit_minutes} ${t('hotel.minutes', 'min')}` : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('hotel.nearestAirport', 'Nearest Airport')}>
                {detail.nearest_airport || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('hotel.airportDrive', 'Airport Drive')}>
                {detail.airport_drive_minutes ? <Tag color="blue"><CarOutlined /> {detail.airport_drive_minutes} {t('hotel.minutes', 'min')}</Tag> : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('hotel.nearbyStore', 'Nearby Convenience Store')} span={2}>
                {detail.has_convenience_store
                  ? <Tag color="cyan"><ShoppingCartOutlined /> 7-Eleven / FamilyMart {t('hotel.walkable', 'within walking distance')}</Tag>
                  : t('hotel.noStore', 'No convenience store nearby')}
              </Descriptions.Item>
            </Descriptions>

            {detail.nearby_pois && detail.nearby_pois.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Divider orientation="left"><ThunderboltOutlined style={{ color: '#fa8c16' }} /> {t('hotel.nearbyFacilities', 'Nearby Facilities')}</Divider>
                <Text type="secondary" style={{ fontSize: 12 }}>{t('hotel.poiHint', 'Click a card to view its location on Google Maps and photos')}</Text>
                <Row gutter={[12, 12]} style={{ marginTop: 8 }}>
                  {detail.nearby_pois.map((p, i) => (
                    <Col xs={12} md={8} key={i}>
                      <a href={p.link || mapsUrl(detail)} target="_blank" rel="noreferrer" style={{ display: 'block', textDecoration: 'none' }}>
                        <Card
                          size="small"
                          hoverable
                          cover={
                            p.photo ? (
                              <img
                                alt={p.name}
                                src={p.photo}
                                style={{ height: 110, objectFit: 'cover', borderRadius: '6px 6px 0 0' }}
                                onError={e => { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <div style={{ height: 110, background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px 6px 0 0' }}>
                                <span style={{ fontSize: 40 }}>{POI_ICONS[p.category] || '📍'}</span>
                              </div>
                            )
                          }
                        >
                          <Space align="start" style={{ width: '100%' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <Text strong style={{ fontSize: 13 }}>{p.name_zh || p.name}</Text>
                              <div>
                                <Tag color={POI_COLORS[p.category]} style={{ marginRight: 4 }}>
                                  {POI_ICONS[p.category] || '📍'} {p.category}
                                </Tag>
                                <Text type="secondary" style={{ fontSize: 12 }}>{t('hotel.approx', 'approx')} {p.distance_km} km</Text>
                              </div>
                              {p.note && (
                                <div style={{ marginTop: 2 }}>
                                  <Text style={{ fontSize: 12, color: '#888' }}>{p.note}</Text>
                                </div>
                              )}
                              <div style={{ marginTop: 2 }}>
                                <Text style={{ fontSize: 12, color: '#1677ff' }}>
                                  <GlobalOutlined /> {t('hotel.googleMaps', 'Google Maps')}
                                </Text>
                              </div>
                            </div>
                          </Space>
                        </Card>
                      </a>
                    </Col>
                  ))}
                </Row>
              </div>
            )}

            {detail.note_zh && (
              <>
                <Divider />
                <Paragraph type="secondary">{detail.note_zh}</Paragraph>
              </>
            )}

            <Divider />
            <Space wrap>
              <Button
                type="primary"
                icon={<EnvironmentOutlined />}
                href={mapsUrl(detail)}
                target="_blank"
              >
                {t('hotel.viewOnMap', 'View on Google Maps')}
              </Button>
              {detail.booking_url && (
                <Button icon={<GlobalOutlined />} href={detail.booking_url} target="_blank">
                  {t('hotel.bookingRates', 'Check Rates on Booking.com')}
                </Button>
              )}
            </Space>
          </>
        )}
      </Drawer>
    </div>
  );
}

export default HotelSearchPage;
